"use client";

import type { PaletteSearchKey } from "@/components/command-palette-search";
import type { ClientCapability } from "@/lib/navigation/portal-routes";
import { createContext, useContext, useEffect, useState } from "react";
import { usePathname, useRouter } from "next/navigation";
import { useAuth } from "@/components/AuthProvider";
import { AwsMark } from "@/components/brand/aws-mark";
import { chatUrl, useChatPath, useChatUiEnabled } from "@/components/chat-ui-provider";
import { DEBOUNCE_MS, fetchPaletteSearchResults, MIN_SEARCH_LENGTH } from "@/components/command-palette-search";
import { McpMark } from "@/components/connectors/connector-logo";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandShortcut,
} from "@/components/ui/command";
import { useConsoleClientNavigation } from "@/components/use-console-client-navigation";
import { useVoiceUiEnabled } from "@/components/voice-ui-provider";
import { getScopedClientId, hasAdminAccess, isSuperAdmin } from "@/lib/auth/user";
import { CONNECTOR_META } from "@/lib/connectors/registry";
import { paletteClientPath, resolvePaletteClientScope } from "@/lib/navigation/palette-scope";
import { consolePath, DEVELOPER_DOCS_URL, GLOBAL_ROUTES, orgPath } from "@/lib/navigation/portal-routes";
import {
  AudioLines,
  BarChart3,
  Bell,
  BookOpen,
  Boxes,
  Building2,
  Cable,
  Cpu,
  FileText,
  FolderClosed,
  History,
  House,
  KeyRound,
  LayoutDashboard,
  LifeBuoy,
  Loader2,
  MessageSquare,
  Network,
  Plug,
  Plus,
  Rocket,
  Route,
  Server,
  Shield,
  UserIcon,
  Wallet,
} from "lucide-react";
import { useTranslations } from "next-intl";
import useSWR from "swr";

interface CommandPaletteContextValue {
  openPalette: () => void;
}

const CommandPaletteContext = createContext<CommandPaletteContextValue | null>(null);

/**
 * Access the global ⌘K palette (e.g. for a visible trigger button).
 * Returns null outside of CommandPaletteProvider (logged-out shells).
 */
export function useCommandPalette(): CommandPaletteContextValue | null {
  return useContext(CommandPaletteContext);
}

/** Debounce a rapidly-changing value. */
function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}

/** Emphasize the first case-insensitive occurrence of `query` inside `text`. */
function Highlight({ text, query }: { text: string; query: string }) {
  const matchIndex = query ? text.toLowerCase().indexOf(query.toLowerCase()) : -1;
  if (matchIndex === -1) return <>{text}</>;
  return (
    <>
      {text.slice(0, matchIndex)}
      <mark className="text-foreground bg-transparent font-semibold">
        {text.slice(matchIndex, matchIndex + query.length)}
      </mark>
      {text.slice(matchIndex + query.length)}
    </>
  );
}

/**
 * Global ⌘K command palette for the authenticated app shell (AIR-611).
 *
 * Wrap the (main) shell once; the provider owns the dialog + keyboard
 * shortcut and exposes `useCommandPalette()` so chrome (the sidebar search
 * bar, the mobile header) can render visible triggers. Contents are
 * role-aware: everyone gets page navigation, quick actions, and literal
 * (zero-AI) search over their own chats and documents (AIR-989); admins
 * additionally get type-to-jump search across clients (super admins) and
 * users (both admin roles), reusing the existing `listClients` / `listUsers`
 * server actions and their auth guards.
 */
export function CommandPaletteProvider({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = useState(false);

  // ⌘K / Ctrl+K toggles the palette anywhere in the shell — including while
  // typing, so it stays reachable from the chat composer.
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() !== "k" || !(e.metaKey || e.ctrlKey) || e.shiftKey || e.altKey) {
        return;
      }
      e.preventDefault();
      setOpen((prev) => !prev);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  return (
    <CommandPaletteContext value={{ openPalette: () => setOpen(true) }}>
      {children}
      <CommandPaletteDialog open={open} onOpenChange={setOpen} />
    </CommandPaletteContext>
  );
}

function CommandPaletteDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const t = useTranslations("commandPalette");
  const router = useRouter();
  const pathname = usePathname();
  const { user } = useAuth();
  // Optional: the palette also mounts on shells where the Connect panel
  // provider may be absent; the action simply hides there.
  // Chat entries hide when the org's chat opt-in or the dedicated-host
  // split (AIR-813) says chat is not served here.
  const chatUiEnabled = useChatUiEnabled();
  const chatPath = useChatPath();
  const voiceUiEnabled = useVoiceUiEnabled();

  const [query, setQuery] = useState("");
  const debouncedQuery = useDebouncedValue(query.trim(), DEBOUNCE_MS);

  const admin = hasAdminAccess(user);
  const superAdmin = isSuperAdmin(user);
  const clientScope = resolvePaletteClientScope(superAdmin, pathname, getScopedClientId(user));
  const consoleClientId = clientScope?.kind === "console" ? clientScope.clientId : null;
  const consoleClient = useConsoleClientNavigation(consoleClientId);
  // Enterprise admins use their membership; super admins inside a client
  // console use the selected client. Platform-wide pages remain unscoped.
  const scopedClientId = clientScope?.clientId ?? null;
  const clientScoped = clientScope !== null;
  const clientVoiceEnabled = clientScope?.kind === "console" ? (consoleClient?.voiceEnabled ?? false) : voiceUiEnabled;
  const clientPath = (capability: ClientCapability) =>
    clientScope ? paletteClientPath(clientScope, capability) : GLOBAL_ROUTES.clients;

  // Literal content search (chats, documents) for everyone plus admin
  // quick-jump (clients, users), once the debounced query is long enough.
  const canSearch = debouncedQuery.length >= MIN_SEARCH_LENGTH;
  const swrKey =
    open && canSearch
      ? ([
          "command-palette-search",
          debouncedQuery,
          superAdmin,
          admin,
          scopedClientId,
          chatUiEnabled,
        ] as PaletteSearchKey)
      : null;
  const { data, isLoading } = useSWR(swrKey, fetchPaletteSearchResults, {
    keepPreviousData: true,
    revalidateOnFocus: false,
  });

  const clientHits = canSearch ? (data?.clients ?? []) : [];
  const userHits = canSearch ? (data?.users ?? []) : [];
  const chatHits = canSearch ? (data?.chats ?? []) : [];
  const documentHits = canSearch ? (data?.documents ?? []) : [];
  const isSearching = canSearch && isLoading;

  // Reset on close so the palette reopens clean.
  const handleOpenChange = (next: boolean) => {
    if (!next) setQuery("");
    onOpenChange(next);
  };

  const navigate = (href: string, { refresh = false } = {}) => {
    handleOpenChange(false);
    router.push(href);
    if (refresh) router.refresh();
  };

  const openExternal = (href: string) => {
    handleOpenChange(false);
    window.open(href, "_blank", "noopener,noreferrer");
  };

  if (!user) return null;

  // Platform super admins must pick a client first; every client-scoped
  // palette uses the same API Keys destination as its sidebar.
  const createApiKeyHref = clientScoped ? `${clientPath("keys")}#api-keys` : null;

  return (
    <CommandDialog
      open={open}
      onOpenChange={handleOpenChange}
      title={t("title")}
      description={t("description")}
      showCloseButton={false}
      className="top-[30%] translate-y-[-30%] rounded-2xl sm:max-w-xl"
    >
      <CommandInput
        placeholder={admin ? t("placeholderAdmin") : t("placeholder")}
        value={query}
        onValueChange={setQuery}
      />
      <CommandList className="max-h-[420px]">
        <CommandEmpty>
          {isSearching ? (
            <span className="text-muted-foreground inline-flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              {t("searching")}
            </span>
          ) : (
            t(admin ? "emptyAdmin" : "empty")
          )}
        </CommandEmpty>

        <CommandGroup heading={t("groups.navigation")}>
          {/* The portal taxonomy follows the current mount: the viewer's org
              for members/admins, or the selected console client for a super
              admin. Platform-wide super-admin pages stay unscoped. */}
          {clientScoped && (
            <>
              <CommandItem
                keywords={["home", "dashboard", "overview"]}
                onSelect={() => navigate(clientPath("dashboard"))}
              >
                <House />
                {t("nav.dashboard")}
              </CommandItem>
              {admin && (
                <CommandItem
                  keywords={["wallet", "billing", "credits", "spend", "financials"]}
                  onSelect={() => navigate(clientPath("wallet"))}
                >
                  <Wallet />
                  {t("nav.financials")}
                </CommandItem>
              )}
              <CommandItem
                keywords={["usage", "reports", "classification"]}
                onSelect={() => navigate(clientPath("analytics"))}
              >
                <BarChart3 />
                {t("nav.analytics")}
              </CommandItem>
              <CommandItem
                keywords={["api keys", "tokens", "credentials"]}
                onSelect={() => navigate(clientPath("keys"))}
              >
                <KeyRound />
                {t("nav.apiKeys")}
              </CommandItem>
              <CommandItem keywords={["org", "team", "members", "users"]} onSelect={() => navigate(clientPath("org"))}>
                <Network />
                {t("nav.members")}
              </CommandItem>
              <CommandItem
                keywords={["notifications", "recap", "emails", "alerts"]}
                onSelect={() => navigate(clientPath("notifications"))}
              >
                <Bell />
                {t("nav.notifications")}
              </CommandItem>
              {admin && (
                <>
                  <CommandItem
                    keywords={["events", "requests", "usage requests", "request log"]}
                    onSelect={() => navigate(clientPath("eventLog"))}
                  >
                    <FileText />
                    {t("nav.eventLog")}
                  </CommandItem>
                  <CommandItem
                    keywords={["activity", "audit", "log"]}
                    onSelect={() => navigate(clientPath("activity"))}
                  >
                    <History />
                    {t("nav.activity")}
                  </CommandItem>
                  <CommandItem
                    keywords={["privacy", "retention", "data controls", "policy"]}
                    onSelect={() => navigate(clientPath("privacy"))}
                  >
                    <Shield />
                    {t("nav.privacy")}
                  </CommandItem>
                </>
              )}
            </>
          )}
          <CommandItem
            keywords={["quickstart", "overview", "cli", "sdk", "getting started"]}
            onSelect={() => navigate(GLOBAL_ROUTES.quickstart)}
          >
            <Rocket />
            {t("nav.quickstart")}
          </CommandItem>
          <CommandItem
            keywords={["integrations", "coding agents", "claude", "codex", "cursor", "office"]}
            onSelect={() => navigate(GLOBAL_ROUTES.quickstartCodingAgents)}
          >
            <Plug />
            {t("nav.quickstartCodingAgents")}
          </CommandItem>
          {clientScoped && (
            <>
              <CommandItem keywords={["models", "llm", "catalog"]} onSelect={() => navigate(clientPath("models"))}>
                <Boxes />
                {t("nav.models")}
              </CommandItem>
              {admin && (
                <CommandItem
                  keywords={["routing", "router", "switchyard", "nvidia", "tier switching"]}
                  onSelect={() => navigate(clientPath("routing"))}
                >
                  <Route />
                  {t("nav.routing")}
                </CommandItem>
              )}
              <CommandItem
                keywords={["gpu", "gpus", "compute", "deploy", "offers"]}
                onSelect={() => navigate(clientPath("gpu"))}
              >
                <Cpu />
                {t("nav.gpu")}
              </CommandItem>
              <CommandItem
                keywords={[
                  "dedicated deployments",
                  "model endpoints",
                  "hugging face",
                  "reserved hardware",
                  "private endpoint",
                ]}
                onSelect={() => navigate(clientPath("endpoints"))}
              >
                <Rocket />
                {t("nav.dedicatedDeployments")}
              </CommandItem>
              <CommandItem
                keywords={[
                  "resources",
                  "my resources",
                  "pods",
                  "machines",
                  "instances",
                  "endpoints",
                  "volumes",
                  "requests",
                ]}
                onSelect={() => navigate(clientPath("resources"))}
              >
                <Server />
                {t("nav.resources")}
              </CommandItem>
              <CommandItem
                keywords={["aws", "aws hub", "aws account", "aws console", "quickstart", "aws cli", "credentials", "playbooks"]}
                onSelect={() => navigate(clientPath("aws"))}
              >
                <AwsMark />
                {t("nav.awsHub")}
              </CommandItem>
              <CommandItem
                keywords={[
                  "ecs",
                  "elastic container service",
                  "containers",
                  "orchestration",
                  "aws",
                  "fargate",
                  "docker",
                  "deploy a container",
                ]}
                onSelect={() => navigate(clientPath("containers"))}
              >
                <AwsMark />
                {t("nav.ecs")}
              </CommandItem>
              <CommandItem
                keywords={["secrets", "secrets manager", "secret", "aws", "credentials", "environment", "valuefrom"]}
                onSelect={() => navigate(clientPath("secrets"))}
              >
                <AwsMark />
                {t("nav.secretsManager")}
              </CommandItem>
              {clientVoiceEnabled && (
                <CommandItem
                  keywords={["voice", "speech", "audio", "realtime", "tts"]}
                  onSelect={() => navigate(clientPath("voice"))}
                >
                  <AudioLines />
                  {t("nav.voice")}
                </CommandItem>
              )}
            </>
          )}
          {chatUiEnabled && (
            <CommandItem keywords={["chat", "conversation", "assistant"]} onSelect={() => navigate(chatPath)}>
              <MessageSquare />
              {t("nav.chat")}
            </CommandItem>
          )}
          {admin && clientScoped && (
            <CommandItem
              keywords={["context", "instructions", "shared files"]}
              onSelect={() => navigate(clientPath("context"))}
            >
              <FolderClosed />
              {t("nav.context")}
            </CommandItem>
          )}
          <CommandItem
            keywords={[
              "tools",
              "mcp",
              "connectors",
              "composio",
              // Every connector is findable by its brand name.
              ...Object.values(CONNECTOR_META).map((meta) => meta.name.toLowerCase()),
            ]}
            onSelect={() => navigate(GLOBAL_ROUTES.tools)}
          >
            <McpMark />
            {t("nav.toolsMcp")}
          </CommandItem>
          <CommandItem keywords={["documents", "files", "uploads"]} onSelect={() => navigate(GLOBAL_ROUTES.documents)}>
            <FolderClosed />
            {t("nav.documents")}
          </CommandItem>
          <CommandItem
            keywords={["docs", "developer docs", "api reference", "guides"]}
            onSelect={() => openExternal(DEVELOPER_DOCS_URL)}
          >
            <BookOpen />
            {t("nav.docs")}
          </CommandItem>
          <CommandItem keywords={["help", "support", "guides"]} onSelect={() => navigate(GLOBAL_ROUTES.help)}>
            <LifeBuoy />
            {t("nav.support")}
          </CommandItem>
          {clientScoped && (
            <CommandItem
              keywords={["integrations", "slack", "github", "microsoft teams", "slack connect"]}
              onSelect={() => navigate(clientPath("integrations"))}
            >
              <Plug />
              {t("nav.integrations")}
            </CommandItem>
          )}
          <CommandItem keywords={["profile", "settings", "account"]} onSelect={() => navigate(GLOBAL_ROUTES.profile)}>
            <UserIcon />
            {t("nav.profile")}
          </CommandItem>
          {superAdmin && (
            <CommandItem
              keywords={["admin", "platform", "dashboard"]}
              onSelect={() => navigate(GLOBAL_ROUTES.platformAdmin)}
            >
              <LayoutDashboard />
              {t("nav.adminDashboard")}
            </CommandItem>
          )}
        </CommandGroup>

        {clientHits.length > 0 && (
          <CommandGroup heading={t("groups.clients")}>
            {clientHits.map((hit) => (
              <CommandItem
                key={hit.id}
                value={`client:${hit.id}`}
                keywords={[hit.name, debouncedQuery]}
                onSelect={() => navigate(`/admin/clients/${hit.id}`)}
              >
                <Building2 />
                <Highlight text={hit.name} query={debouncedQuery} />
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {userHits.length > 0 && (
          <CommandGroup heading={t("groups.users")}>
            {userHits.map((hit) => (
              <CommandItem
                key={hit.id}
                value={`user:${hit.id}`}
                keywords={[hit.name ?? "", hit.email, debouncedQuery]}
                onSelect={() =>
                  navigate(
                    hit.clientId
                      ? superAdmin
                        ? consolePath(hit.clientId, "org")
                        : orgPath("org")
                      : "/admin/system-users",
                  )
                }
              >
                <UserIcon />
                <span className="flex min-w-0 flex-1 items-baseline gap-2">
                  <span className="truncate">
                    <Highlight text={hit.name || hit.email} query={debouncedQuery} />
                  </span>
                  {hit.name && (
                    <span className="text-muted-foreground truncate text-xs">
                      <Highlight text={hit.email} query={debouncedQuery} />
                    </span>
                  )}
                </span>
                {superAdmin && hit.clientName && (
                  <span className="text-muted-foreground ml-auto shrink-0 text-xs">{hit.clientName}</span>
                )}
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {chatHits.length > 0 && (
          <CommandGroup heading={t("groups.chats")}>
            {chatHits.map((hit) => (
              <CommandItem
                key={hit.id}
                value={`chat:${hit.id}`}
                keywords={[hit.title, hit.snippet, debouncedQuery]}
                onSelect={() => navigate(chatUrl(chatPath, hit.id))}
              >
                <MessageSquare />
                <span className="flex min-w-0 flex-1 items-baseline gap-2">
                  {/* The title is the anchor: let it use up to ~2/3 of the row
                      before truncating; the snippet fills whatever remains. */}
                  <span className="max-w-[65%] shrink-0 truncate">
                    <Highlight text={hit.title} query={debouncedQuery} />
                  </span>
                  <span className="text-muted-foreground truncate text-xs">
                    <Highlight text={hit.snippet} query={debouncedQuery} />
                  </span>
                </span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {documentHits.length > 0 && (
          <CommandGroup heading={t("groups.documents")}>
            {documentHits.map((hit) => (
              <CommandItem
                key={hit.id}
                value={`document:${hit.id}`}
                keywords={[hit.name, debouncedQuery]}
                onSelect={() => navigate("/documents")}
              >
                <FileText />
                <span className="min-w-0 flex-1 truncate">
                  <Highlight text={hit.name} query={debouncedQuery} />
                </span>
                {hit.category && <span className="text-muted-foreground ml-auto shrink-0 text-xs">{hit.category}</span>}
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        <CommandGroup heading={t("groups.actions")}>
          {chatUiEnabled && (
            <CommandItem
              keywords={["new", "chat", "start", "compose"]}
              onSelect={() => navigate(chatPath, { refresh: true })}
            >
              <Plus />
              {t("actions.newChat")}
              <CommandShortcut>⇧⌘O</CommandShortcut>
            </CommandItem>
          )}
          {createApiKeyHref && (
            <CommandItem
              keywords={["api key", "token", "credentials", "create"]}
              onSelect={() => navigate(createApiKeyHref)}
            >
              <KeyRound />
              {t("actions.createApiKey")}
            </CommandItem>
          )}
          {/* The old Connect action is the Developers quickstart now; the
              slide-over panel only remains for ?connect= deep links. */}
          <CommandItem
            keywords={["connect", "sdk", "api", "curl", "claude", "codex", "cursor", "gateway", "quickstart"]}
            onSelect={() => navigate(GLOBAL_ROUTES.quickstart)}
          >
            <Cable />
            {t("actions.developerQuickstart")}
          </CommandItem>
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}
